import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { formatCompactMoney } from '@/lib/money';
import { financeColor } from '../lib/financeColors';
import { FinanceChartFilters, type FinanceChartFilterProps } from './FinanceChartFilters';
import { FinanceRefreshing } from './FinanceNotReady';

/** Align overview series labels with top KPI names (Phase 0 D1 — 6 KPI override). */
export function overviewSeriesDisplayName(name: string, category?: string): string {
  const key = (category ?? name).toUpperCase();
  if (key === 'GROSS' || name.toLowerCase() === 'revenue') return 'Gross Transaction Value';
  if (key === 'PAYOUT' || name.toLowerCase().includes('payout')) return 'Creator Earnings';
  return name;
}

export function TransactionOverviewCard({
  categories,
  series,
  total,
  changePercent,
  revenueSharePercent,
  payoutSharePercent,
  currency,
  range,
  onRangeChange,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  startDate,
  endDate,
  isLoading = false,
}: {
  categories: string[];
  series: { name: string; category?: string; values: number[]; total: number }[];
  total: number;
  changePercent: number | null;
  revenueSharePercent: number;
  payoutSharePercent: number;
  currency: string;
} & FinanceChartFilterProps) {
  const isDark = useIsDarkMode();
  const compactMoney = (value: number | string) => formatCompactMoney(Number(value), currency);
  const axisMoney = (value: number | string) =>
    formatCompactMoney(Number(value), currency, { withCode: false });

  const displaySeries = useMemo(
    () =>
      series.map((item) => ({
        ...item,
        displayName: overviewSeriesDisplayName(item.name, item.category),
      })),
    [series],
  );

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      grid: tone(CHART_INK.grid, isDark),
      axis: tone(CHART_INK.axis, isDark),
      surface: tone(CHART_INK.surface, isDark),
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
        valueFormatter: compactMoney,
      },
      legend: { show: false },
      grid: { left: 6, right: 12, top: 18, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories,
        axisLine: { lineStyle: { color: ink.axis } },
        axisTick: { show: false },
        axisLabel: { color: ink.muted, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: ink.muted, fontSize: 10, formatter: axisMoney },
        splitLine: { lineStyle: { color: ink.grid, type: 'dashed' } },
      },
      series: displaySeries.map((item) => {
        const color = financeColor(item.category ?? item.name);
        return {
          name: item.displayName,
          type: 'line',
          smooth: 0.45,
          symbol: 'circle',
          showSymbol: false,
          lineStyle: { width: 2, color },
          itemStyle: { color },
          emphasis: { focus: 'series', scale: 1.4 },
          areaStyle: { opacity: 0.12, color },
          data: item.values,
        };
      }),
    };
  }, [categories, currency, displaySeries, isDark]);

  return (
    <Card className="min-w-0 shadow-none">
      <CardHeader className="space-y-3 p-5 pb-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-sky-400">
              Gross Transaction Value Overview · {currency}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-2xl font-semibold tracking-tight">
                {formatCompactMoney(total, currency)}
              </span>
              {changePercent != null && (
                <Badge
                  className={
                    changePercent >= 0
                      ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-400'
                      : 'border-rose-500/30 bg-rose-500/15 text-rose-400'
                  }
                >
                  {changePercent >= 0 ? '+' : ''}
                  {changePercent.toFixed(1)}%
                </Badge>
              )}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Total is Gross Transaction Value for the selected filters
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <FinanceChartFilters
              range={range}
              onRangeChange={onRangeChange}
              countryCode={countryCode}
              onCountryCodeChange={onCountryCodeChange}
              availableCountryCodes={availableCountryCodes}
              {...(startDate ? { startDate } : {})}
              {...(endDate ? { endDate } : {})}
              dateAriaLabel="Change Gross Transaction Value Overview date range"
              countryAriaLabel="Change Gross Transaction Value Overview creator country"
            />
            <div className="flex flex-wrap justify-end gap-4 text-xs text-muted-foreground">
              {displaySeries.map((item) => {
                const isPayout =
                  (item.category ?? item.name).toUpperCase() === 'PAYOUT' ||
                  item.name.toLowerCase().includes('payout');
                return (
                  <span key={item.displayName} className="inline-flex items-center gap-1.5">
                    <span
                      className="size-2 rounded-full"
                      style={{ backgroundColor: financeColor(item.category ?? item.name) }}
                    />
                    {item.displayName}{' '}
                    {Math.round(isPayout ? payoutSharePercent : revenueSharePercent)}%
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-1">
        <FinanceRefreshing isLoading={isLoading}>
          <ReactECharts option={option} notMerge style={{ height: 420, width: '100%' }} />
        </FinanceRefreshing>
      </CardContent>
    </Card>
  );
}
